import { useEffect, useState, useSyncExternalStore } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HistoryIcon from "@mui/icons-material/History";
import CommentIcon from "@mui/icons-material/Comment";
import CalculateIcon from "@mui/icons-material/Calculate";
import { Box, Button } from "@mui/material";
import { useThemeContext } from "../globalContext/global-context";
import type { SurveySession } from "../session/SurveySession";
import { StoredValuesPanel } from "./StoredValuesPanel";

export const SurveyUtilityBar = ({
  session,
  onComment,
}: {
  session: SurveySession;
  onComment?: () => void;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const attempt = snapshot.attempt;
  const [showStoredValues, setShowStoredValues] = useState(false);
  const complete = attempt?.question.question.type === "complete";

  useEffect(() => {
    if (attempt?.question.undoable !== true || !snapshot.canUndo || complete)
      return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "Backspace" ||
        event.defaultPrevented ||
        event.repeat ||
        event.isComposing ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      )
        return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest(
          "input, textarea, select, [contenteditable], [role='dialog']",
        )
      )
        return;
      event.preventDefault();
      session.undoPrevious(attempt);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [attempt, complete, session, snapshot.canUndo]);
  const buttonStyle = {
    color: theme.tokens.color.text.inverse,
    minWidth: 0,
    padding: `${theme.tokens.spacing["1"]}px ${theme.tokens.spacing["2"]}px`,
    gap: `${theme.tokens.spacing["1"]}px`,
    "&.Mui-disabled": { color: theme.tokens.color.text.inverse, opacity: 0.45 },
  };

  if (attempt?.question.undoable !== true) return null;

  return (
    <Box
      data-testid="survey-utility-bar"
      sx={{
        position: "relative",
        zIndex: 3,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: `${theme.tokens.spacing["1"]}px`,
        minHeight: 44,
        paddingInline: `${theme.tokens.spacing["2"]}px`,
        backgroundColor: theme.tokens.color.text.primary,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <Button
          data-testid="auto-default-response"
          aria-label={theme.copy.actions.autoAnswer}
          disabled={!attempt || !snapshot.canSubmit || complete}
          onClick={() => attempt && session.submitDefault(attempt)}
          sx={buttonStyle}
        >
          <PlayArrowIcon /> {theme.copy.actions.auto}
        </Button>
        {!complete && (
          <Button
            disabled={!attempt || !snapshot.canUndo}
            onClick={() => attempt && session.undoPrevious(attempt)}
            sx={buttonStyle}
          >
            <HistoryIcon /> {theme.copy.actions.undo}
          </Button>
        )}
        {onComment && (
          <Button
            data-testid="open-comment-modal"
            onClick={onComment}
            sx={buttonStyle}
          >
            <CommentIcon /> {theme.copy.actions.comment}
          </Button>
        )}
        {snapshot.storedValues.length > 0 && (
          <Button
            data-testid="open-variable-store-floating"
            onClick={() => setShowStoredValues((value) => !value)}
            sx={buttonStyle}
          >
            <CalculateIcon />{" "}
            {showStoredValues
              ? theme.copy.actions.hide
              : theme.copy.actions.show}
          </Button>
        )}
      </Box>
      {showStoredValues && snapshot.storedValues.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            right: 0,
            maxWidth: "100%",
            maxHeight: "50vh",
            overflowY: "auto",
            backgroundColor: theme.tokens.color.surface.card,
            color: theme.tokens.color.text.primary,
            padding: `${theme.tokens.spacing["4"]}px`,
            boxShadow: theme.tokens.elevation["3"],
            borderRadius: `${theme.tokens.shape.radius.md}px`,
          }}
        >
          <StoredValuesPanel
            entries={snapshot.storedValues}
            canDelete={Boolean(attempt && snapshot.canUndo)}
            onDelete={(entry) =>
              attempt && session.deleteStoredValue(attempt, entry)
            }
          />
        </Box>
      )}
    </Box>
  );
};
